import React, { useRef } from 'react';

function Icon({ children }) {
  return (
    <svg
      className="menu-manager-toolbar-icon"
      viewBox="0 0 24 24"
      width="15"
      height="15"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const icons = {
  refresh: (
    <Icon>
      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
    </Icon>
  ),
  template: (
    <Icon>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8" />
      <path d="M8 17h5" />
    </Icon>
  ),
  import: (
    <Icon>
      <path d="M12 3v12" />
      <path d="m7 11 5 5 5-5" />
      <path d="M5 21h14" />
    </Icon>
  ),
  export: (
    <Icon>
      <path d="M12 16V4" />
      <path d="m7 8 5-5 5 5" />
      <path d="M5 21h14" />
    </Icon>
  ),
  backup: (
    <Icon>
      <path d="M21 8v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
      <path d="M3 8h18l-2-4H5z" />
      <path d="M10 12h4" />
    </Icon>
  ),
  ratings: (
    <Icon>
      <path d="m12 3 2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 15.4 7.2 17.9l.9-5.4L4.2 8.7l5.4-.8z" />
    </Icon>
  ),
  add: (
    <Icon>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </Icon>
  ),
  sides: (
    <Icon>
      <path d="M4 9h16" />
      <path d="M6 9c.6 6 2.6 9 6 9s5.4-3 6-9" />
    </Icon>
  ),
  allergens: (
    <Icon>
      <path d="M12 3 2.8 19h18.4z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </Icon>
  ),
};

const Toolbar = ({
  onRefresh,
  onImport,
  onExport,
  onBackupAndClear,
  onResetRatings,
  onDownloadTemplate,
  onAddCategory,
  onAddItem,
  onAddSubcategory,
  onManageUniversalSides,
  onManageAllergens,
}) => {
  const fileRef = useRef(null);

  const triggerImport = () => fileRef.current?.click();
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await onImport?.(file);
    e.target.value = ''; // reset
  };

  const Button = ({ children, onClick, title, danger, icon }) => (
    <button
      type="button"
      className={`menu-manager-toolbar-btn${danger ? " menu-manager-toolbar-btn--danger" : ""}`}
      onClick={onClick}
      title={title}
    >
      {icon}
      <span>{children}</span>
    </button>
  );

  return (
    <div className="menu-manager-toolbar-inner">
      <Button icon={icons.refresh} onClick={onRefresh} title="Reload the menu">Refresh</Button>
      {onDownloadTemplate && (
        <Button
          icon={icons.template}
          onClick={onDownloadTemplate}
          title="Download a blank Excel workbook with Categories, Subcategories, Sides, Allergens, and Items sheets"
        >
          Template
        </Button>
      )}
      {onImport && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept=".json,.csv,.xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
          <Button icon={icons.import} onClick={triggerImport} title="Import a filled template workbook">
            Import
          </Button>
        </>
      )}
      <Button icon={icons.export} onClick={onExport} title="Export the full menu workbook">Export</Button>
      {onBackupAndClear && (
        <Button
          danger
          icon={icons.backup}
          onClick={onBackupAndClear}
          title="Download Excel + SQL as a ZIP, then wipe the entire menu"
        >
          Backup & Clear
        </Button>
      )}
      {onResetRatings && (
        <Button
          danger
          icon={icons.ratings}
          onClick={onResetRatings}
          title="Set every menu item rating to 3 and clear stored rating history"
        >
          Reset Ratings
        </Button>
      )}
      <span className="menu-manager-toolbar-sep" aria-hidden="true" />
      <Button icon={icons.add} onClick={onAddCategory} title="Add a main category">Add Category</Button>
      <Button icon={icons.add} onClick={onAddSubcategory} title="Add a subcategory">Add Subcategory</Button>
      <Button icon={icons.add} onClick={onAddItem} title="Add a menu item">Add Item</Button>
      {onManageUniversalSides && (
        <Button icon={icons.sides} onClick={onManageUniversalSides} title="Manage shared sides used across menu items">
          Sides
        </Button>
      )}
      {onManageAllergens && (
        <Button icon={icons.allergens} onClick={onManageAllergens} title="Manage allergen library and descriptions">
          Allergens
        </Button>
      )}
    </div>
  );
};

export default Toolbar;
